import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
export const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [jwt, setJwt] = useState(() => localStorage.getItem("token") || null);
  const [loading, setLoading] = useState(!!jwt);
  const [authError, setAuthError] = useState("");
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return localStorage.getItem("pendingEmail") || "";
    } catch {
      return "";
    }
  });
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(300);

    const [userTick, setUserTick] = useState(0);
    const [creditsTick, setCreditsTick] = useState(0);
    const [forceReset, setForceReset] = useState(false);

    const setUserAndTick = (newUser) => {
      setUser({ ...newUser });
      setUserTick(tick => tick + 1);
      setCreditsTick(t => t + 1);
    };

    const refreshUser = () => {
      if (!jwt) {
        setUser(null);
        setForceReset(false);
        return;
      }
      axios
        .get(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${jwt}` }
        })
        .then(res => {
          setUserAndTick(res.data);
          setForceReset(res.data.must_reset_password === true);
        })
        .catch(() => {
          setUser(null);
          setJwt(null);
          localStorage.removeItem("token");
          setForceReset(false);
        });
    };

    useEffect(() => {
      if (!jwt) {
        setUser(null);
        setLoading(false);
        setForceReset(false);
        return;
      }
      setLoading(true);
      axios
        .get(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${jwt}` }
        })
        .then(res => {
          setUserAndTick(res.data);
          setForceReset(res.data.must_reset_password === true);
          setLoading(false);
        })
        .catch(() => {
          setUser(null);
          setJwt(null);
          localStorage.removeItem("token");
          setLoading(false);
          setForceReset(false);
        });
    }, [jwt]);

  // --- 30-Minute Inactivity Auto-Logout WITH WARNING ---
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const warningCountdownRef = useRef(null);
  const INACTIVITY_LIMIT_MINUTES = 30;
  const WARNING_BEFORE_MINUTES = 5;

  useEffect(() => {
    if (!user || !jwt) return;

    const cleanup = () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (warningCountdownRef.current) clearInterval(warningCountdownRef.current);
      setShowInactivityWarning(false);
      setWarningCountdown(300);
    };

    const logoutDueToInactivity = () => {
      setShowInactivityWarning(false);
      setWarningCountdown(300);
      setAuthError("Logged out due to inactivity.");
      logout();
    };

    const resetTimer = () => {
      cleanup();
      warningTimeoutRef.current = setTimeout(() => {
        setShowInactivityWarning(true);
        setWarningCountdown(300);
        warningCountdownRef.current = setInterval(() => {
          setWarningCountdown(prev => {
            if (prev <= 1) {
              clearInterval(warningCountdownRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        idleTimeoutRef.current = setTimeout(() => {
          setShowInactivityWarning(false);
          setWarningCountdown(300);
          logoutDueToInactivity();
        }, WARNING_BEFORE_MINUTES * 60 * 1000);
      }, (INACTIVITY_LIMIT_MINUTES - WARNING_BEFORE_MINUTES) * 60 * 1000);
    };

    const activityHandler = () => {
      if (showInactivityWarning) {
        setShowInactivityWarning(false);
        setWarningCountdown(300);
      }
      resetTimer();
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(event => window.addEventListener(event, activityHandler));
    resetTimer();

    return () => {
      cleanup();
      events.forEach(event => window.removeEventListener(event, activityHandler));
    };
    // eslint-disable-next-line
  }, [user, jwt]);

  // --- End Inactivity Logic ---

  // FIXED: Now returns error string if login fails
  const login = async (email, password) => {
    setAuthError("");
    try {
      const res = await axios.post(`${API_BASE}/login`, { email, password });
      setJwt(res.data.access_token);
      localStorage.setItem("token", res.data.access_token);
      setAuthError("");
      setUserAndTick({ email });
      return true;
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err?.message ||
        "Login failed. Please check your credentials.";
      setAuthError(detail);
      return detail; // Return the error string!
    }
  };

  const register = async (email, firstName, lastName, captchaToken, eulaAgreed, website = "") => {
    setAuthError("");
    try {
      await axios.post(`${API_BASE}/register`, {
        user: {
          email,
          first_name: firstName,
          last_name: lastName,
          // Password is no longer sent at registration time; user will set it
          // after confirming their e-mail via the reset/creation flow.
          eula_agreed: !!eulaAgreed, // must be true to pass backend guard
          website: website || "",
        },
        captcha: captchaToken,
      });

      // On success, behave exactly as RegistrationForm expects:
      // - pendingConfirmation true
      // - pendingEmail set to the registered e-mail
      setPendingConfirmation(true);
      setPendingEmail(email);
      try {
        localStorage.setItem("pendingEmail", email || "");
      } catch {
        // non-fatal: if storage fails, confirmation screen just won't show the address
      }
      setAuthError("");
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Email already registered, but not confirmed") {
        setAuthError("E-mail address already registered. Resend code?");
      } else if (detail === "Email already registered") {
        setAuthError("User is already registered. Please login instead.");
      } else {
        setAuthError(detail || "Registration failed. Please try again.");
      }
      return false;
    }
  };

  const confirmEmail = async (token) => {
    setAuthError("");
    try {
      const res = await axios.get(`${API_BASE}/confirm-email?token=${token}`);
        setPendingConfirmation(false);
        setPendingEmail("");
        try {
          localStorage.removeItem("pendingEmail");
        } catch {
          // non-fatal
        }
        setAuthError("");
        return res.data;
    } catch (err) {
      setAuthError(
        err.response?.data?.detail ||
          "Email confirmation failed. Token invalid or expired."
      );
      return null; // Always return something
    }
  };

  const logout = () => {
    setUser(null);
    setJwt(null);
    setAuthError("");
    setForceReset(false);
    localStorage.removeItem("token");
  };

  // --- Inactivity Warning Modal (simple version) ---
  const InactivityWarningModal = () => {
    if (!showInactivityWarning) return null;
    const min = Math.floor(warningCountdown / 60);
    const sec = warningCountdown % 60;
    return (
      <div style={{
        position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.4)", zIndex: 9999, display: "flex",
        alignItems: "center", justifyContent: "center"
      }}>
        <div style={{
          background: "#fff", padding: "2rem", borderRadius: "1rem", boxShadow: "0 2px 12px #333"
        }}>
          <div className="text-lg font-bold mb-2">Are you still there?</div>
          <div className="mb-4">
            You will be logged out in <b>{min}:{sec.toString().padStart(2, "0")}</b> due to inactivity.
            <br />
            Any mouse/keyboard/touch action will keep you logged in.
          </div>
          {/* Brand blue button; only color changed */}
          <button
            className="text-white px-4 py-2 rounded w-full hover:opacity-90"
            style={{ backgroundColor: "#00a7e1" }}
            onClick={() => {
              setShowInactivityWarning(false);
              setWarningCountdown(300);
            }}
          >
            I'm still here
          </button>
        </div>
      </div>
    );
  };
  // --- End Inactivity Warning ---

  return (
    <AuthContext.Provider
      value={{
        user,
        jwt,
        login,
        logout,
        register,
        confirmEmail,
        authError,
        setAuthError,
        loading,
        pendingConfirmation,
        pendingEmail,
        setPendingConfirmation,
        setPendingEmail,
        userTick,
        setUserAndTick,
        creditsTick,
        forceReset,
        setForceReset,
		refreshUser,
      }}
    >
      <InactivityWarningModal />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
